import { useState } from 'react';
import * as XLSX from 'xlsx';
import useAllActiveMarkets, { UseFetchAllActiveMarketsParams } from './useAllActiveMarkets';
import { formatter } from '../../../../utils/currencyFormatter';

/* eslint-disable @typescript-eslint/no-unused-vars */

interface IFormatData {
  Region: string;
  Producer: string | undefined;
  'Wine name': string;
  Vintage: number;
  'Unit size': string;
  'Market value': string;
  Score: string | number;
  Spread: string;
  'Highest bid': string;
  'Lowest offer': string;
  'Last trade price': string | null;
  'Last trade date': string | null;
}

const formatNumber = (number?: number | null) => {
  return number ? formatter.format(Number(number)) : '';
};

const getSpread = (spreadNumber: number | null | undefined) => {
  if (spreadNumber === null || !spreadNumber) return '';
  if (spreadNumber > 0) return `+${spreadNumber}%`;
  return `-${spreadNumber}%`;
};

const removeTimestamp = (text = '') => {
  if (!text) return '';
  const dateOnly = text.split(' ')[0];
  return dateOnly;
};

const useExportAllActiveMarkets = (fetchAllActiveMarketsSetUp: UseFetchAllActiveMarketsParams) => {
  const [isLoading, setIsLoading] = useState(false);
  const { startFetch } = useAllActiveMarkets(fetchAllActiveMarketsSetUp);
  const exportActiveMarketsToXlsx = async () => {
    setIsLoading(true);
    const activeMarkets = await startFetch();
    const formatData = activeMarkets.map((x) => {
      return {
        Region: x.region,
        Producer: x.producer,
        'Wine name': x.wineName,
        Vintage: x.vintage,
        'Unit size': `${x?.unitCount}x${x?.unitSize}`,
        'Market value': formatNumber(x?.marketValue),
        Score: x?.score ?? '',
        Spread: getSpread(x?.spread),
        'Highest bid': formatNumber(x?.highestBid?.price),
        'Lowest offer': formatNumber(x?.lowestOffer?.price),
        'Last trade price': formatNumber(x?.lastTradePrice),
        'Last trade date': removeTimestamp(x?.lastTradeDateTime ?? ''),
      };
    });

    formatData.sort((a, b) => {
      const keys: (keyof IFormatData)[] = ['Region', 'Producer', 'Wine name', 'Vintage', 'Unit size'];
      /* eslint-disable no-restricted-syntax */
      for (const key of keys) {
        const valA = a[key];
        const valB = b[key];

        const isValANullish = valA === null || valA === undefined || valA === '';
        const isValBNullish = valB === null || valB === undefined || valB === '';

        if (isValANullish && !isValBNullish) return 1;
        if (!isValANullish && isValBNullish) return -1;

        if (key === 'Vintage') {
          const numA = parseInt(String(valA), 10);
          const numB = parseInt(String(valB), 10);
          if (numA !== numB) return numA - numB;
        } else {
          if ((valA as string) < (valB as string)) return -1;
          if ((valA as string) > (valB as string)) return 1;
        }
      }
      /* eslint-enable no-restricted-syntax */
      return 0;
    });

    const worksheet = XLSX.utils.json_to_sheet(formatData);

    const headerLength = Object.keys(formatData[0]).length;
    const lastCol = XLSX.utils.encode_col(headerLength - 1);
    worksheet['!autofilter'] = { ref: `A1:${lastCol}1` };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, 'Cultx Active Markets.xlsx');

    setIsLoading(false);
  };

  return { exportActiveMarketsToXlsx, isLoading };
};

/* eslint-enable @typescript-eslint/no-unused-vars */

export default useExportAllActiveMarkets;
